import { useCallback, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { addMonths } from 'date-fns';
import { postUserProductAssign } from 'src/apis/userProducts/UserProductAssign';
import { useAppSelector } from 'src/hooks/redux';
import { ProductAssignmentSubmitPayload, UserProductAssignResponse } from 'src/models/ProductAssignment';
import { selectUser } from 'src/redux/user/userSlice';
import { buildProductAssignmentPayload } from '../buildAssignmentPayload';
import {
    DURATION_PRESET_OPTIONS,
    FormValues,
    DurationPreset,
    PRODUCT_IDS_VALID,
    PRODUCT_ID_WITH_ENTRY_RULES,
} from '../Form/formConstants';

const atStartOfDay = (d: Date) => {
    const next = new Date(d);
    next.setHours(0, 0, 0, 0);
    return next;
};

const monthsFromPreset = (preset: DurationPreset): number | null => {
    if (preset === '1m') return 1;
    if (preset === '3m') return 3;
    if (preset === '6m') return 6;
    return null;
};

const endDateForStartAndPreset = (start: Date, preset: DurationPreset): Date => {
    const n = monthsFromPreset(preset);
    if (n === null) {
        return atStartOfDay(start);
    }
    return atStartOfDay(addMonths(start, n));
};

const inferPresetFromDates = (start: Date, end: Date): DurationPreset => {
    const e = atStartOfDay(end).getTime();
    const pairs: Array<[DurationPreset, number]> = [
        ['1m', 1],
        ['3m', 3],
        ['6m', 6],
    ];
    for (const [preset, m] of pairs) {
        if (e === atStartOfDay(addMonths(start, m)).getTime()) {
            return preset;
        }
    }
    return 'other';
};

const buildInitialValues = (): FormValues => {
    const today = atStartOfDay(new Date());
    const preset: DurationPreset = '1m';
    return {
        product_id: '8',
        email: '',
        entry_type: '',
        allowed_leads: '',
        date_start: today,
        duration_preset: preset,
        date_end: endDateForStartAndPreset(today, preset),
    };
};

const isValidDate = (value: unknown): value is Date =>
    value instanceof Date && !Number.isNaN(value.getTime());

const validationSchema = yup.object({
    product_id: yup
        .string()
        .trim()
        .required('El producto es obligatorio')
        .oneOf([...PRODUCT_IDS_VALID], 'Debe seleccionar un producto válido'),
    email: yup
        .string()
        .trim()
        .required('El correo es obligatorio')
        .email('Correo electrónico inválido'),
    entry_type: yup.string().when('product_id', {
        is: PRODUCT_ID_WITH_ENTRY_RULES,
        then: (schema) =>
            schema
                .trim()
                .required('El tipo de ingreso es obligatorio')
                .oneOf(['pago', 'free', 'cooldown'], 'Seleccione un tipo de ingreso válido'),
        otherwise: (schema) => schema.notRequired(),
    }),
    allowed_leads: yup.string().when(['product_id', 'entry_type'], {
        is: (product_id: string, entry_type: string) =>
            product_id === PRODUCT_ID_WITH_ENTRY_RULES && entry_type === 'free',
        then: (schema) =>
            schema
                .trim()
                .required('Los leads permitidos son obligatorios')
                .test('is-positive-int', 'Ingrese un entero mayor o igual a 1', (val) => {
                    if (val === undefined || val === null) return false;
                    const n = Number(String(val).trim());
                    return !Number.isNaN(n) && Number.isInteger(n) && n >= 1;
                }),
        otherwise: (schema) => schema.notRequired(),
    }),
    duration_preset: yup
        .string()
        .trim()
        .required('La duración es obligatoria')
        .oneOf(
            DURATION_PRESET_OPTIONS.map((o) => o.value),
            'Seleccione una duración válida',
        ),
    date_start: yup
        .mixed<Date>()
        .required('La fecha de inicio es obligatoria')
        .test('is-date', 'Seleccione una fecha de inicio válida', isValidDate),
    date_end: yup
        .mixed<Date>()
        .required('La fecha de fin es obligatoria')
        .test('is-date', 'Seleccione una fecha de fin válida', isValidDate)
        .test(
            'gte-date-start',
            'La fecha de fin no puede ser anterior a la de inicio',
            function gteStart(endDate) {
                const { date_start: startDate } = this.parent as { date_start?: Date };
                if (!isValidDate(endDate) || !isValidDate(startDate)) return false;
                return atStartOfDay(endDate).getTime() >= atStartOfDay(startDate).getTime();
            },
        ),
});

const assignmentSubmitErrorMessage = (err: unknown): string => {
    if (typeof err === 'object' && err !== null && 'response' in err) {
        const data = (err as { response?: { data?: unknown } }).response?.data;
        if (typeof data === 'string' && data.trim()) return data;
        if (data && typeof data === 'object' && 'message' in data) {
            const m = (data as { message: unknown }).message;
            if (typeof m === 'string' && m.trim()) return m;
        }
    }
    if (err instanceof Error && err.message) return err.message;
    return 'No se pudo completar la asignación. Intente de nuevo.';
};

export function useProductAssignmentForm() {
    const initialValues = useMemo(() => buildInitialValues(), []);
    const profile = useAppSelector(selectUser);
    const [resultModal, setResultModal] = useState<{
        open: boolean;
        requestPayload: ProductAssignmentSubmitPayload | null;
        response: UserProductAssignResponse | null;
        error: string | null;
    }>({ open: false, requestPayload: null, response: null, error: null });

    const closeResultModal = useCallback(() => {
        setResultModal({ open: false, requestPayload: null, response: null, error: null });
    }, []);

    const formik = useFormik<FormValues>({
        initialValues,
        validationSchema,
        validateOnMount: false,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            const token = (profile.token ?? '').trim();
            if (!token) {
                setResultModal({
                    open: true,
                    requestPayload: null,
                    response: null,
                    error: 'No hay sesión activa. Vuelva a iniciar sesión e intente de nuevo.',
                });
                setSubmitting(false);
                return;
            }
            const payload = buildProductAssignmentPayload(values, profile.email);
            try {
                const data = await postUserProductAssign(payload, token);
                setResultModal({ open: true, requestPayload: payload, response: data, error: null });
                resetForm({ values: buildInitialValues() });
            } catch (err: unknown) {
                const message = assignmentSubmitErrorMessage(err);
                setResultModal({ open: true, requestPayload: payload, response: null, error: message });
            } finally {
                setSubmitting(false);
            }
        },
    });

    const showFieldError = (field: keyof FormValues) =>
        Boolean(formik.errors[field] && (formik.touched[field] || formik.submitCount > 0));

    const handleDurationPresetChange = (value: string) => {
        const preset = value as DurationPreset;
        formik.setFieldValue('duration_preset', preset, false);
        formik.setFieldTouched('duration_preset', true, false);
        const n = monthsFromPreset(preset);
        if (n !== null) {
            const start = formik.values.date_start;
            formik.setFieldValue('date_end', endDateForStartAndPreset(start, preset), false);
        }
    };

    const handleDateStartChange = (date: Date | null) => {
        if (!date) {
            return;
        }
        const normalized = atStartOfDay(date);
        const preset = formik.values.duration_preset;
        formik.setFieldValue('date_start', normalized, false);

        let nextEnd: Date = formik.values.date_end;
        const n = monthsFromPreset(preset);
        if (n !== null) {
            nextEnd = atStartOfDay(addMonths(normalized, n));
            formik.setFieldValue('date_end', nextEnd, false);
        } else if (nextEnd && atStartOfDay(nextEnd).getTime() < normalized.getTime()) {
            nextEnd = normalized;
            formik.setFieldValue('date_end', nextEnd, false);
        }

        const inferred = inferPresetFromDates(normalized, nextEnd);
        if (inferred !== formik.values.duration_preset) {
            formik.setFieldValue('duration_preset', inferred, false);
        }
        formik.setFieldTouched('date_start', true, false);
    };

    const handleDateEndChange = (date: Date | null) => {
        if (!date) {
            return;
        }
        const normalized = atStartOfDay(date);
        const start = formik.values.date_start;
        const inferred = inferPresetFromDates(start, normalized);
        formik.setFieldValue('date_end', normalized, false);
        if (inferred !== formik.values.duration_preset) {
            formik.setFieldValue('duration_preset', inferred, false);
        }
        formik.setFieldTouched('date_end', true, false);
    };

    const handleEntryTypeChange = (value: string) => {
        formik.setFieldValue('entry_type', value, false);
        if (value !== 'free') {
            formik.setFieldValue('allowed_leads', '', false);
        }
        formik.setFieldTouched('entry_type', true, false);
    };

    return {
        formik,
        showFieldError,
        handleDurationPresetChange,
        handleDateStartChange,
        handleDateEndChange,
        handleEntryTypeChange,
        resultModal,
        closeResultModal,
    };
}
